import {
  netQuantity,
  units,
  velocityOf,
  type LazyProductDTO,
  type ProductKpiInventoryDTO,
  type ProductKpiSalesDTO,
  type SalesWindow,
} from "../../api/product";

/**
 * The products grid read as families.
 *
 * A variation group is a product in the shop's sense — one bed, in its colours
 * and sizes — and the grid's flat list hides that: the six James nightstands
 * sit wherever their rankings put them. In the family view a group with more
 * than one member becomes one parent row over its members. The parent is a
 * reading aid, not a record: it has no SKU, no status, no price, and is never
 * selected, exported or opened on its own. The figures that add up (units,
 * stock, sales) are summed on it; rankings take the best member; anything
 * per-unit is blank.
 *
 * Groups of one stay plain rows, so single products look the same in both
 * views, and the parent's own columns are set so that no filter matches it on
 * its own — the grid filters from the leaves, and a family is shown exactly
 * when one of its products is.
 */

export interface Family {
  members: ProductRow[];
  colours: number;
  sizes: number;
  /** What the family sells per week: the members' weekly averages added up. */
  weekly_avg: number;
}

/**
 * A grid row: a product, or a family over its products. `ranks` is the rank of
 * a top-level row of the family view among all top-level rows — families and
 * single products together — by units in that window; the products inside a
 * family carry their own product rank, and a flat-view row has no `ranks`.
 */
export type ProductRow = LazyProductDTO & { family?: Family; ranks?: Partial<Record<SalesWindow, number>> };

/** The windows the grid ranks on, product and family alike. */
export const RANKED_WINDOWS: SalesWindow[] = ["four_weeks", "twelve_weeks", "ytd"];

export function isFamily(row: ProductRow): boolean {
  return row.family !== undefined;
}

/** Rows for the family view: one parent per multi-member group, singles as they are. */
export function familyRows(products: LazyProductDTO[]): ProductRow[] {
  const groups = new Map<number, LazyProductDTO[]>();
  const singles: ProductRow[] = [];
  for (const product of products) {
    const group = product.variation_group;
    if (group === null || group === undefined) {
      singles.push(product);
      continue;
    }
    const members = groups.get(group);
    if (members) members.push(product);
    else groups.set(group, [product]);
  }
  const rows: ProductRow[] = singles.map((product) => ({ ...product }));
  for (const [group, members] of groups) {
    if (members.length === 1) {
      rows.push({ ...(members[0] as ProductRow) });
    } else {
      rows.push(familyRow(group, members));
    }
  }
  rankRows(rows);
  return rows;
}

/**
 * Ranks the top-level rows against one another by units, per window: a family
 * by its members' units added up, a single product by its own — one list, so
 * "#3" means the same thing whichever kind of row carries it. Ties share a
 * rank, the way the KPI's product rankings do not; close enough for a reading
 * aid, and stable between two builds of the same data.
 */
function rankRows(rows: ProductRow[]): void {
  for (const window of RANKED_WINDOWS) {
    const order = [...rows].sort((a, b) => units(b.kpi_sales, window) - units(a.kpi_sales, window));
    let rank = 0;
    let previous: number | null = null;
    order.forEach((row, index) => {
      const value = units(row.kpi_sales, window);
      if (value !== previous) {
        rank = index + 1;
        previous = value;
      }
      (row.ranks ??= {})[window] = rank;
    });
  }
}

function familyRow(group: number, members: LazyProductDTO[]): ProductRow {
  const sorted = [...members].sort((a, b) => sku(a).localeCompare(sku(b)));
  const live = sorted.filter((m) => m.status === "AVAILABLE");
  const representative = live[0] ?? sorted[0]!;
  // The face of the family is its best seller — the member selling most per
  // week, among those with a picture; the first available one failing that.
  const pictured = sorted.filter((m) => m.image);
  const bestSeller = [...(pictured.length > 0 ? pictured : sorted)].sort((a, b) => velocityOf(b) - velocityOf(a))[0] ?? representative;
  const colours = new Set<string>();
  const sizes = new Set<string>();
  for (const member of sorted) {
    for (const variation of member.variations ?? []) {
      const value = (variation.value ?? "").trim().toLowerCase();
      if (!value) continue;
      if (variation.type === "COLOR") colours.add(value);
      if (variation.type === "SIZE") sizes.add(value);
    }
  }
  const row: ProductRow = {
    ...representative,
    // Negative, so it can never collide with a product id in the row model.
    id: -group,
    // Blank, so no filter on these columns matches the family by itself.
    status: "" as LazyProductDTO["status"],
    type: "" as LazyProductDTO["type"],
    shopify_excluded: false,
    metadata: { ...(representative.metadata ?? {}), sku: "", upc: "" } as LazyProductDTO["metadata"],
    description_en: { ...(representative.description_en ?? {}), product_name: familyName(sorted) } as LazyProductDTO["description_en"],
    variations: [],
    image: bestSeller.image ?? representative.image ?? null,
    pricing: null,
    created_at: earliest(sorted.map((m) => m.created_at)),
    // Unions, so a category, collection or tag filter finds the family through any member.
    category: representative.category ?? null,
    collections: union(sorted.flatMap((m) => m.collections ?? [])),
    tags: union(sorted.flatMap((m) => m.tags ?? [])),
    kpi_inventory: sumInventory(sorted),
    kpi_sales: sumSales(sorted),
    family: {
      members: sorted,
      colours: colours.size,
      sizes: sizes.size,
      weekly_avg: sorted.reduce((sum, member) => sum + velocityOf(member), 0),
    },
  };
  return row;
}

/** A product's velocity, or a family's: its members' added up. */
export function rowWeeklyAvg(row: ProductRow): number {
  return row.family ? row.family.weekly_avg : velocityOf(row);
}

/**
 * Weeks of cover, for a product or a family — the family's stock over the
 * family's velocity, with the planner's 999 sentinel for stock that never
 * sells (see `remainingWeeks`).
 */
export function rowRemainingWeeks(row: ProductRow): number {
  const velocity = rowWeeklyAvg(row);
  const net = netQuantity(row);
  if (velocity === 0 && net > 0) return 999;
  if (velocity === 0) return 0;
  return Math.max(Math.trunc(net / velocity), 0);
}

/**
 * The name the family shares: each member's English name minus a trailing
 * ", tail" naming its own colour or size (any part of a "Size|Colour" tail counts — a legacy
 * row sometimes lacks the colour value) (the legacy "Platform Bed Frame,
 * Queen|Walnut" style), then the most common result.
 */
export function familyName(members: LazyProductDTO[]): string {
  const votes = new Map<string, number>();
  for (const member of members) {
    const name = (member.description_en?.product_name ?? "").trim();
    if (!name) continue;
    const base = stripVariationTail(name, member);
    votes.set(base, (votes.get(base) ?? 0) + 1);
  }
  let best = "";
  let bestVotes = -1;
  for (const [name, count] of votes) {
    if (count > bestVotes || (count === bestVotes && name.length < best.length)) {
      best = name;
      bestVotes = count;
    }
  }
  return best;
}

const SIZE_WORDS = /^(twin|twin xl|full|double|full\/double|queen|king|california king)$/i;

function stripVariationTail(name: string, member: LazyProductDTO): string {
  const comma = name.lastIndexOf(",");
  if (comma <= 0) return name;
  const tail = name.slice(comma + 1).trim();
  const norm = (value: string) => value.trim().toLowerCase().replace(/_/g, "/");
  const values = new Set((member.variations ?? []).map((v) => norm(v.value ?? "")).filter(Boolean));
  const parts = tail.split("|").map((part) => part.trim());
  const owned = parts.some((part) => values.has(norm(part)) || SIZE_WORDS.test(part));
  return owned ? name.slice(0, comma).trim() : name;
}

function sku(product: LazyProductDTO): string {
  return product.metadata?.sku ?? "";
}

function earliest(dates: Array<string | undefined>): string | undefined {
  const known = dates.filter((d): d is string => Boolean(d)).sort();
  return known[0];
}

function union<T extends { id: number }>(items: T[]): T[] {
  const seen = new Map<number, T>();
  for (const item of items) if (!seen.has(item.id)) seen.set(item.id, item);
  return [...seen.values()];
}

function sumInventory(members: LazyProductDTO[]): ProductKpiInventoryDTO {
  const total: Record<string, number> = {};
  const warehouses: Record<string, { quantity: number; reserved_quantity: number; in_order_quantity: number }> = {};
  const add = (key: string, value: number | null | undefined) => {
    total[key] = (total[key] ?? 0) + (value ?? 0);
  };
  for (const member of members) {
    const inventory = member.kpi_inventory;
    if (!inventory) continue;
    for (const key of [
      "quantity",
      "reserved_quantity",
      "in_order_quantity",
      "planned_quantity",
      "production_quantity",
      "inventory_value",
      "percent_of_inventory_quantity",
      "percent_of_inventory_value",
    ]) {
      add(key, (inventory as unknown as Record<string, number | undefined>)[key]);
    }
    for (const [warehouse, bucket] of Object.entries(inventory.warehouse_quantities ?? {})) {
      const sum = warehouses[warehouse] ?? { quantity: 0, reserved_quantity: 0, in_order_quantity: 0 };
      sum.quantity += bucket.quantity ?? 0;
      sum.reserved_quantity += bucket.reserved_quantity ?? 0;
      sum.in_order_quantity += bucket.in_order_quantity ?? 0;
      warehouses[warehouse] = sum;
    }
  }
  return { ...total, warehouse_quantities: warehouses } as unknown as ProductKpiInventoryDTO;
}

/**
 * Units and money summed; the weekly averages and velocities summed too, since
 * a family's velocity is what its members sell together; the rank takes the
 * best member, weeks on sale and the first sale the oldest, the last sale the
 * most recent.
 */
function sumSales(members: LazyProductDTO[]): ProductKpiSalesDTO {
  const total: Record<string, number | string | null> = {};
  for (const member of members) {
    const sales = member.kpi_sales as unknown as Record<string, number | string | null | undefined> | null | undefined;
    if (!sales) continue;
    for (const [key, value] of Object.entries(sales)) {
      if (typeof value !== "number") continue;
      if (key === "sales_rank") {
        const current = total[key];
        total[key] = typeof current === "number" && current > 0 ? Math.min(current, value) : value;
      } else if (key === "first_sale_aging_days" || key === "weeks_on_sale") {
        const current = total[key];
        total[key] = typeof current === "number" ? Math.max(current, value) : value;
      } else if (key === "last_sale_aging_days") {
        const current = total[key];
        total[key] = typeof current === "number" ? Math.min(current, value) : value;
      } else {
        total[key] = ((total[key] as number | undefined) ?? 0) + value;
      }
    }
    const last = sales.last_sale_date;
    if (typeof last === "string" && (!total.last_sale_date || last > (total.last_sale_date as string))) {
      total.last_sale_date = last;
    }
  }
  return total as unknown as ProductKpiSalesDTO;
}
